import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import _, { compose, mapObject, constant, map, findWhere } from 'underscore';
import { withScope, QueryParam } from '@bingads-webui-react/scope-service';
import { withDefaultStyles } from '@bingads-webui-react/with-default-styles';
import { withPermissions } from '@bingads-webui-react/hoc-with-permissions';
import { withI18nConsumer } from '@bingads-webui-react/i18n-context';
import { getLocalStorage } from '@bingads-webui/storage';
import { withDFMCache } from '@bingads-webui/dfm-cache';
import { withPreference } from '@bingads-webui-react/preference-context';
import { withSiteMap, siteMapPropType } from '@bingads-webui-campaign-react/with-site-map';
import { perfMarkerPropTypes } from '@bingads-webui/performance-marker';
import { Stack } from '@fluentui/react';
import { withCcJsData } from '@bingads-webui-cc-react/js-data-rest';
import { Constants as CCUIConstants } from '@bingads-webui-clientcenter/entity-utils';
import { FAC_FEATURE_ID } from '@bingads-webui-campaign/recommendation-core';

import { StyledActionList } from '../components/action-list';
import { getChooseObjectiveStyles } from '../styles';
import {
  objectivesConfig,
  toolsConfig,
  navigateSelectedTools,
  navigateSelectedObjective,
  withCreationHeader,
  shoppingCampaignTypes,
  campaignTypes,
  objectivesForPerfMax,
  adImpressionsCampaignTypes,
  getProductData,
} from '../helper';
import { StyledChooseAdsPanel } from '../components/choose-ads-panel';

class ChooseObjective extends React.PureComponent {
  constructor(props) {
    super(props);

    this.localStorage = getLocalStorage();
    this.isLocalStorageSupported = this.localStorage.supportsStorage();
    if (this.isLocalStorageSupported) {
      this.localStorage.invalidate('CampaignObjective');
    }

    this.state = {
      isPanelOpen: false,
      selectedItemId: null,
      isMMCSetup: null,
      couponDetails: null,
    };
  }

  componentDidMount() {
    const {
      perfMarker,
      prefetchChunks,
      scope,
      ccJsData,
      permissions,
      i18n,
      serverContext,
    } = this.props;
    if (perfMarker) {
      perfMarker.done();
    }
    if (typeof prefetchChunks === 'function') {
      prefetchChunks();
    }
    const { uid, cid, aid } = scope.values;
    const {
      CurrentAccount: {
        CurrencyCode,
      },
    } = serverContext;
    if (permissions.dynamic.FACForShopping) {
      this.getFACData({
        ccJsData,
        aid,
        i18n,
        CurrencyCode,
      });
    }
    this.getProductData = _.once(() => {
      const getProductDataWithCache = withDFMCache(getProductData, 'getProductScopeDFM', this);
      const getProductDataPromise = Promise.resolve(getProductDataWithCache(cid, aid, uid));
      getProductDataPromise.then((data) => {
        const hasAnyStores = !_.isEmpty(data) && !_.isEmpty(data.ProductCollectionAccounts);
        this.setState({
          isMMCSetup: hasAnyStores,
        });
      });
    });
    this.setSelectedItemFromQueryString();
  }

  getFACData = ({
    ccJsData, aid, i18n, CurrencyCode,
  }) => {
    ccJsData.FeatureAdoptionCoupon
      .findAll({ aid })
      .then((coupons) => {
        if (!_.isArray(coupons) || coupons.length === 0) {
          return [];
        }
        return coupons;
      })
      .catch(() => [])
      .then(coupons => _.filter(
        coupons,
        ({ Status, FeatureId }) => FeatureId === parseInt(FAC_FEATURE_ID.SHOPPING_CAMPAIGN, 10)
        && (
          Status === CCUIConstants.RecommendationAdoptionStatus.NotInProgess
          || Status === CCUIConstants.RecommendationAdoptionStatus.Active
        )
      ))
      .then((coupons) => {
        if (coupons.length > 0) {
          this.setState({
            couponDetails: {
              ...coupons[0],
              CouponValueWithCurrency: i18n.formatCurrency(coupons[0].CouponValue, CurrencyCode),
            },
          });
        }
      });
  };

  getDescription = i18n => i18n.getString(_TL_('A successful campaign needs a clear goal, and setting your goal here lets us suggest the most helpful Microsoft Advertising features for your campaign. You\'ll still have access to all Microsoft Advertising features regardless of the goal you choose.'));

  get showPerfMaxCampaignTypeOptions() {
    const { permissions } = this.props;
    return permissions.dynamic.PerformanceMaxCampaigns && _.contains(objectivesForPerfMax, this.state.selectedItemId);
  }

  setSelectedItemFromQueryString = () => {
    const { scope, i18n, permissions } = this.props;
    const selectedItemKeyFromSearch = scope.values && scope.values.selectedCampaignObjectiveKey;
    const objectiveItems = objectivesConfig({ i18n, permissions }).filter(item => item.isEnabled);

    if (objectiveItems.some(item => item.id === selectedItemKeyFromSearch)) {
      this.handleSelectedObjective({ id: selectedItemKeyFromSearch });
    }
  };

  showPanel = () => {
    this.setState({ isPanelOpen: true });
    if (this.props.permissions.dynamic.PerformanceMaxCampaigns) {
      this.getProductData();
    }
  };

  closePanel = () => {
    this.setState({ isPanelOpen: false, selectedItemId: null });
  };

  handleSelectedObjective = ({ id: itemId }) => {
    const {
      config, permissions, scope, siteMap,
    } = this.props;
    const isSmartCampaign = itemId === 'SmartCampaign';
    const isHotelBooking = itemId === 'HotelBooking';
    const isAppInstall = itemId === 'AppInstalls';
    const isMSStoreAdsGAWorkflow = itemId === 'MSStoreAds';

    if (isMSStoreAdsGAWorkflow) {
      scope.history.push({
        pathname: '/campaign/vnext/createcampaign/msstoreads',
        search: scope.history.location.search,
      });
      return;
    }

    if (isHotelBooking) {
      const IsRenameHotelToLodgingEnabled = this.props.permissions && this.props.permissions.dynamic && this.props.permissions.dynamic.IsRenameHotelToLodgingEnabled;
      scope.history.push({
        pathname: IsRenameHotelToLodgingEnabled ? '/campaign/vnext/createcampaign/lodging' : '/campaign/vnext/createcampaign/hotel',
        search: scope.history.location.search,
      });
      return;
    }

    if (isSmartCampaign) {
      const { uid, cid, aid } = scope.values;
      window.location.href = `/campaign/smart?cid=${cid}&aid=${aid}&uid=${uid}#/CreateCampaign`;
      return;
    }

    if (isAppInstall) {
      navigateSelectedObjective({
        isShopping: false,
        isAudience: false,
        isSmartShopping: false,
        siteMap: [findWhere(siteMap, { id: 'campaign-create' })],
        scope,
      });
    }

    const isShopping = itemId.indexOf('ShoppingCampaign') !== -1;
    this.setState({ isPanelOpen: false, selectedItemId: itemId });
    config.set('CampaignObjective', itemId);

    if (this.isLocalStorageSupported) {
      this.localStorage.set('CampaignObjective', itemId);
    }

    if ((permissions.IsVideoAdsForCampaignEnabled && !permissions.dynamic.XandrVideoCampaigns) && itemId === 'VideoCampaign') {
      navigateSelectedObjective({
        isVideo: true, isAudience: true, siteMap: [findWhere(siteMap, { id: 'campaign-create' })], scope, permissions,
      });
    } else if (permissions.IsManualCpmEnabled && !permissions.dynamic.XandrDisplayAdsCampaigns && itemId === 'AdImpressions') {
      // only native ads enabled
      navigateSelectedObjective({
        isAudience: true, siteMap: [findWhere(siteMap, { id: 'campaign-create' })], scope,
      });
    } else if (!permissions.IsManualCpmEnabled && permissions.dynamic.XandrDisplayAdsCampaigns && itemId === 'AdImpressions') {
      // only display ad enabled
      navigateSelectedObjective({
        isAudience: true, isDisplayAds: true, siteMap: [findWhere(siteMap, { id: 'campaign-create' })], scope,
      });
    } else if (
      (permissions.IsAIMCampaignEnabled && itemId !== 'SponsoredShoppingCampaign' &&
      !(permissions.IsDomesticSimplifiedChineseEnabled && itemId === 'ShoppingCampaign')) ||
      (permissions.IsSmartShoppingCampaignEnabled && !permissions.IsDomesticSimplifiedChineseEnabled && !permissions.dynamic.SSCDeprecation && itemId === 'ShoppingCampaign') ||
      (permissions.IsDealEnabledForMsAds && itemId === 'MediaPackageCampaign') ||
      (permissions.IsManualCpmEnabled && permissions.dynamic.XandrDisplayAdsCampaigns && itemId === 'AdImpressions') ||
      (permissions.dynamic.XandrVideoCampaigns && itemId === 'VideoCampaign') ||
      (itemId === 'SponsoredShoppingCampaign')) {
      this.showPanel();
    } else {
      navigateSelectedObjective({
        isShopping, isAudience: false, siteMap: [findWhere(siteMap, { id: 'campaign-create' })], scope,
      });
    }
  };

  handleNavigation = (selectedType) => {
    const { scope, siteMap, permissions } = this.props;
    if (!selectedType) {
      return;
    }

    if (this.state.selectedItemId === 'VideoCampaign') {
      navigateSelectedObjective({
        isVideo: true, isAudience: true, siteMap: [findWhere(siteMap, { id: 'campaign-create' })], scope, permissions,
      });
      return;
    }

    if (this.state.selectedItemId === 'MediaPackageCampaign') {
      navigateSelectedObjective({
        isMediaPackage: true, siteMap: [findWhere(siteMap, { id: 'campaign-create' })], scope,
      });
      return;
    }

    if (this.state.selectedItemId === 'AdImpressions') {
      if (selectedType === adImpressionsCampaignTypes.displayAds) {
        navigateSelectedObjective({
          isAudience: true, isDisplayAds: true, siteMap: [findWhere(siteMap, { id: 'campaign-create' })], scope,
        });
        return;
      }

      navigateSelectedObjective({
        isAudience: true, siteMap: [findWhere(siteMap, { id: 'campaign-create' })], scope,
      });
      return;
    }

    if (this.state.selectedItemId === 'SponsoredShoppingCampaign') {
      navigateSelectedObjective({
        sponsoredBrandAdType: selectedType,
        siteMap: [findWhere(siteMap, { id: 'campaign-create' })],
        scope,
      });

      return;
    }

    navigateSelectedObjective({
      isShopping: this.state.selectedItemId.indexOf('ShoppingCampaign') !== -1,
      isAudience: selectedType === 'AudienceAds',
      isNewAudience: permissions.IsNewStaticAudienceCampaignCreationEnabled,
      isNewSearch: permissions.dynamic.SearchCampaignCreationRevamp,
      isSmartShopping: selectedType === shoppingCampaignTypes.smart,
      isPerfMax: selectedType === campaignTypes.perfMax,
      siteMap: [findWhere(siteMap, { id: 'campaign-create' })],
      scope,
    });
  };

  renderActionList = () => {
    const { i18n, permissions } = this.props;
    const objectives = map(objectivesConfig({ i18n, permissions }), item => ({
      onClick: this.handleSelectedObjective,
      isSelected: (!!this.state.selectedItemId) && this.state.selectedItemId === item.id,
      FACInfo: this.state.couponDetails,
      ...item,
    }));
    const tools = map(toolsConfig({ i18n, permissions }), item => ({
      onClick: params => navigateSelectedTools({
        ...this.props,
        ...params,
      }),
      isSelected: (!!this.state.selectedItemId) && this.state.selectedItemId === item.id,
      ...item,
    }));

    return (
      <Fragment>
        <StyledActionList actionItems={objectives} title={i18n.getString(_TL_('What\'s the goal of this campaign?'))} />
        <StyledActionList actionItems={tools} title={i18n.getString(_TL_('Research & other tools'))} />
      </Fragment>
    );
  };

  render() {
    const {
      classes,
      serverContext,
      i18n,
      preferenceService,
      permissions,
    } = this.props;

    return (
      <div className={`${classes.chooseObjective} choose-objective`}>
        <div className="choose-objective-page">
          <div className={`${classes.pageContent} page-content`}>
            <div className={`${classes.description} choose-objective-description`}>
              <Stack horizontal tokens={{ childrenGap: 30 }}>
                <Stack.Item grow={3}>{this.getDescription(i18n)}</Stack.Item>
              </Stack>
            </div>
            {this.renderActionList()}
          </div>
        </div>
        <StyledChooseAdsPanel
          config={this.props.config}
          itemId={this.state.selectedItemId}
          preferenceService={preferenceService}
          serverContext={serverContext}
          isSmartShoppingCampaignEnabled={permissions.IsSmartShoppingCampaignEnabled && !permissions.dynamic.SSCDeprecation}
          isOpen={this.state.isPanelOpen}
          onClose={this.closePanel}
          handleNavigation={this.handleNavigation}
          showPerfMaxCampaignTypeOptions={this.showPerfMaxCampaignTypeOptions}
          isMMCSetup={this.state.isMMCSetup}
        />
      </div>
    );
  }
}

ChooseObjective.propTypes = {
  classes: PropTypes.shape(mapObject(
    getChooseObjectiveStyles({}, {}),
    constant(PropTypes.string.isRequired)
  )).isRequired,
  i18n: PropTypes.shape({
    getString: PropTypes.func,
  }).isRequired,
  preferenceService: PropTypes.shape({}).isRequired,
  permissions: PropTypes.shape({
    IsAIMCampaignEnabled: PropTypes.bool.isRequired,
    IsDynamicSearchAdsV1Enabled: PropTypes.bool.isRequired,
    IsAdultAdsEnabled: PropTypes.bool.isRequired,
    IsUserAppInstallAdsAllowed: PropTypes.bool.isRequired,
    IsEnhancedShoppingCampaignAllowed: PropTypes.bool.isRequired,
    IsSmartShoppingCampaignEnabled: PropTypes.bool.isRequired,
    IsVideoAdsForCampaignEnabled: PropTypes.bool.isRequired,
    IsDealEnabledForMsAds: PropTypes.bool.isRequired,
    IsAmplifyEnabled: PropTypes.bool.isRequired,
    IsNewStaticAudienceCampaignCreationEnabled: PropTypes.bool.isRequired,
    dynamic: PropTypes.shape({
      PerformanceMaxCampaigns: PropTypes.bool,
      XandrVideoCampaigns: PropTypes.bool,
      XandrDisplayAdsCampaigns: PropTypes.bool,
      FACForShopping: PropTypes.bool,
      DisplayPlusCampaigns: PropTypes.bool,
      SSCDeprecation: PropTypes.bool,
      SearchCampaignCreationRevamp: PropTypes.bool,
    }),
  }).isRequired,
  // config: e.g. component/config/index file should be inject from the options
  // eslint-disable-next-line react/forbid-prop-types
  config: PropTypes.object.isRequired,
  siteMap: siteMapPropType.isRequired,
  scope: PropTypes.instanceOf(QueryParam).isRequired,
  perfMarker: perfMarkerPropTypes.isRequired,
  prefetchChunks: PropTypes.func.isRequired,
  serverContext: PropTypes.shape({
    CurrentAccount: PropTypes.shape({
      CurrencyCode: PropTypes.string.isRequired,
    }).isRequired,
  }).isRequired,
  ccJsData: PropTypes.shape({}).isRequired,
};

const wrap = compose(
  TempComponent => withDefaultStyles(TempComponent, getChooseObjectiveStyles),
  withScope,
  withI18nConsumer,
  withPreference,
  withPermissions,
  withSiteMap,
  withCcJsData,
  TempComponent => withCreationHeader(TempComponent, undefined, undefined, false)
);

export const StyledCampaignChooseObjective = wrap(ChooseObjective);
